<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div>
    <div :class="['oap-eventful-text-input', { '-focused': isInputFocused }]">
      <slot />

      <input
        ref="input"
        v-model="text"
        type="text"
        :placeholder="placeholderText"
        :aria-label="placeholderText"
        required
        @keydown.enter="submit"
        @focus="onFocus"
        @blur="onBlur"
        @keyup="onKeyup"
      />

      <button type="submit" @click="submit">
        <svg aria-hidden="true">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="svgIconId"></use>
        </svg>
        <span class="is-sr-only">{{ submitButtonSrLabel }}</span>
      </button>
    </div>

    <div v-if="suggestedQuestions.length" class="oap-beauty-genius__suggested-questions">
      <button
        v-for="(suggestedQuestion, index) in suggestedQuestions"
        :key="index"
        class="oap-beauty-genius__question"
        @click="submit(suggestedQuestion)"
      >
        {{ suggestedQuestion.text }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import eventBus from '@refapp/eventbus-js';
import { AnalyticsHandler } from '@Foundation';
import { BEAUTY_GENIUS_TYPE_IN_TAGGING_EVENT } from '@Feature/OapBeautyGeniusWrapper/code/Scripts/settings';

const props = defineProps({
  placeholderText: { type: String, required: true },
  submitButtonSrLabel: { type: String, required: true },
  iframeTriggerEventName: { type: String, default: 'beauty-genius-iframe:open' },
  modalOpenEventName: { type: String, default: 'beauty-genius-modal:open' },
  autofocus: { type: Boolean, default: true },
  analytics: { type: Object, default: () => {} },
  svgIconId: { type: String, default: '#arrow-top-right' },
  suggestedQuestions: { type: Array, default: () => [] },
});

const input = ref(null);
const isInputFocused = ref(false);

const text = ref('');

const CSS_CLASS_TO_HIDE_PRODUCT_TOOLBAR = 'oap-beauty-genius__suggested-questions--is-shown';

let tag;

onMounted(async () => {
  eventBus.on(props.modalOpenEventName, () => {
    if (props.autofocus) {
      setTimeout(() => {
        input.value.focus();
      }, 300);
    }
  });

  tag = { ...props.analytics };

  eventBus.on(BEAUTY_GENIUS_TYPE_IN_TAGGING_EVENT, (event) => {
    if (event) {
      tag.event_name = event.payload.event_name;
      tag.action = event.payload.action;
    }
  });
});

function showSuggestionQuestions() {
  document.body.classList.add(CSS_CLASS_TO_HIDE_PRODUCT_TOOLBAR);
}

function hideSuggestionQuestions() {
  document.body.classList.remove(CSS_CLASS_TO_HIDE_PRODUCT_TOOLBAR);
}

function onFocus() {
  isInputFocused.value = true;

  if (props.suggestedQuestions.length) {
    showSuggestionQuestions();
  }
}

function onBlur() {
  isInputFocused.value = false;

  if (props.suggestedQuestions.length) {
    // this timeout is needed to handle the click event on the button/bubble properly
    setTimeout(() => {
      hideSuggestionQuestions();
    }, 250);
  }
}

function onKeyup() {
  text.value.length ? hideSuggestionQuestions() : showSuggestionQuestions();
}

function onSubmitQuestion(questionText) {
  eventBus.emit(props.iframeTriggerEventName, {
    payload: questionText,
    analytics: {
      cta_name: props.analytics.cta_name,
    },
  });

  if (props.analytics) {
    tag.type = 'userActionEvent';
    tag.link_url = props.analytics.link_url;

    try {
      AnalyticsHandler.getAnalyticsHandler().push(tag);
    } catch (e) {
      /* istanbul ignore next */
      console.warn('Could not push to dataLayer', e);
    }
  }
}

function submit(suggestedQuestion) {
  // Text from the v-model, the one from the input field
  if (text.value.length) {
    onSubmitQuestion(text.value);

    // Clear the input
    text.value = '';
  } else if (suggestedQuestion.text) {
    // Text from the suggested question bubble
    onSubmitQuestion(suggestedQuestion.text);
  }
}
</script>
